// Upcoming payroll impact, derived from credit-service repayment schedules
// (the source of truth for future loan/advance deductions). Amounts here are
// SCHEDULED — payroll confirms the actual deduction at calculation time
// (min-net-pay clamping etc.), so the UI must label them as such.
import type { ScheduleLine } from '@/features/credit/types';

export interface UpcomingSource {
  facilityId: string;
  /** Product name shown to the employee (falls back to a generic label upstream). */
  label: string;
  currency: string;
  schedule: ScheduleLine[];
}

export interface UpcomingItem {
  facilityId: string;
  label: string;
  amountDue: number;
  currency: string;
  /** Last pending installment of its facility — "final repayment". */
  final: boolean;
}

export interface UpcomingPeriod {
  period: string; // "YYYY-MM"
  items: UpcomingItem[];
  /** Sum across items; null when currencies differ (never aggregate unlike currencies). */
  total: number | null;
  currency: string | null;
}

const isPending = (s: ScheduleLine) => s.status !== 'paid' && s.status !== 'skipped';

/** Group pending installments across facilities by due period, earliest first. */
export function groupUpcoming(sources: UpcomingSource[], maxPeriods = 3): UpcomingPeriod[] {
  const byPeriod = new Map<string, UpcomingItem[]>();

  for (const src of sources) {
    const pending = src.schedule.filter(isPending).sort((a, b) => a.duePeriod.localeCompare(b.duePeriod));
    pending.forEach((line, i) => {
      if (!line.duePeriod) return;
      const items = byPeriod.get(line.duePeriod) ?? [];
      items.push({
        facilityId: src.facilityId,
        label: src.label,
        // Remaining due on a partial line, otherwise the scheduled amount.
        amountDue: Math.max(line.amountDue - line.amountPaid, 0),
        currency: src.currency,
        final: i === pending.length - 1,
      });
      byPeriod.set(line.duePeriod, items);
    });
  }

  return Array.from(byPeriod.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(0, maxPeriods)
    .map(([period, items]) => {
      const currencies = new Set(items.map((i) => i.currency));
      const single = currencies.size === 1;
      return {
        period,
        items,
        total: single ? items.reduce((s, i) => s + i.amountDue, 0) : null,
        currency: single ? items[0].currency : null,
      };
    });
}
